import React, { useMemo, useState } from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import LoadingDots from '../../LoadingDots/LoadingDots';
import { collectImageSources } from '../../../utils/imagePreload';
import { useImagePreload } from '../../../hooks/useImagePreload';
import ScrollFadeWrapper from '../../ScrollFadeWrapper/ScrollFadeWrapper';
import Image from '../../Image/Image';

const richTextSx = {
  fontFamily: 'Inter, sans-serif',
  color: '#162F3B',
  '& p': { margin: 0, mb: '1em' },
  '& p:last-of-type': { mb: 0 },
  '& strong': { fontWeight: 700 },
  '& a': {
    color: '#162F3B',
    fontWeight: 700,
    textDecoration: 'underline',
  },
  '& a:hover': { opacity: 0.85 },
};

const matchesFilter = (card, activeFilter) => {
  if (!activeFilter || activeFilter.key === 'all') return true;
  if (activeFilter.matchTag)
    return Boolean(card.tags?.includes(activeFilter.matchTag));
  return Boolean(card.isHippoPartner);
};

const LifeInsuranceCompaniesListMobileComponent = ({ data }) => {
  const filters = data?.filters ?? [];
  const cards = useMemo(
    () => (data?.cards ?? []).filter((card) => card.logo),
    [data]
  );
  const [activeFilterKey, setActiveFilterKey] = useState(
    filters[0]?.key ?? 'all'
  );

  const imageSources = useMemo(() => collectImageSources(data), [data]);
  const areImagesReady = useImagePreload(imageSources);

  const activeFilter = filters.find((f) => f.key === activeFilterKey);

  const filterCounts = useMemo(() => {
    const counts = {};
    filters.forEach((filter) => {
      counts[filter.key] = cards.filter((card) =>
        matchesFilter(card, filter)
      ).length;
    });
    return counts;
  }, [filters, cards]);

  if (!areImagesReady) {
    return (
      <Box
        sx={{
          width: '100%',
          minHeight: '220px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <LoadingDots variant="otp" />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {filters.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            gap: '12px',
            mb: '20px',
            overflowX: 'auto',
          }}
        >
          {filters.map((filter) => {
            const isActive = filter.key === activeFilterKey;
            return (
              <Box
                key={filter.key}
                component="button"
                onClick={() => setActiveFilterKey(filter.key)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  border: 'none',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  borderRadius: '20px',
                  padding: '8px 16px',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: isActive ? 700 : 600,
                  fontSize: '0.875rem',
                  lineHeight: '1.5rem',
                  letterSpacing: '-0.002em',
                  color: isActive ? '#FFFFFF' : '#162F3B',
                  backgroundColor: isActive ? '#162F3B' : '#E7E7E7',
                  transition: 'background-color 0.2s ease, color 0.2s ease',
                }}
              >
                {filter.label}
                <Box
                  component="span"
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.875rem',
                    lineHeight: '1.5rem',
                    letterSpacing: '-0.002em',
                    fontWeight: isActive ? 700 : 600,
                    color: '#A2ACB1',
                  }}
                >
                  {filterCounts[filter.key] ?? 0}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <Box
        sx={{
          width: '100%',
          backgroundColor: '#FFFFFF',
          borderRadius: '24px',
          boxShadow: '0px 1px 2px 0px #A2ACB133, 0px 12px 24px -4px #162F3B1F',
          boxSizing: 'border-box',
          padding: '24px 20px',
        }}
      >
        {data?.title && (
          <Typography
            component="h2"
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '1.375rem',
              lineHeight: '1.625rem',
              letterSpacing: '-0.003em',
              verticalAlign: 'middle',
              color: '#162F3B',
              mb: '12px',
              textWrap: 'auto',
              whiteSpace: 'pre-line',
            }}
          >
            {data.title}
          </Typography>
        )}

        {data?.body && (
          <Typography
            component="div"
            sx={{
              ...richTextSx,
              fontWeight: 400,
              fontSize: '0.9375rem',
              lineHeight: '1.4rem',
              letterSpacing: '-0.002em',
              verticalAlign: 'middle',
              whiteSpace: 'pre-line',
              mb: '24px',
            }}
            dangerouslySetInnerHTML={{ __html: data.body }}
          />
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {cards.map((card, index) => {
            const isVisible = matchesFilter(card, activeFilter);
            return (
              <Collapse key={index} in={isVisible} timeout={300} unmountOnExit>
                <ScrollFadeWrapper>
                  <Box
                    sx={{
                      position: 'relative',
                      backgroundColor: '#FFFFFF',
                      boxShadow:
                        '0px 1px 2px 0px #A2ACB133, 0px 12px 24px -4px #162F3B1F',
                      borderRadius: '20px',
                      padding: '16px',
                      boxSizing: 'border-box',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '16px',
                    }}
                  >
                    {card.isHippoPartner && (
                      <Box
                        sx={{
                          alignSelf: 'flex-end',
                          width: 'fit-content',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#C7CA00',
                          color: '#162F3B',
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          lineHeight: '1.25rem',
                          letterSpacing: '0.01em',
                          textAlign: 'center',
                          whiteSpace: 'nowrap',
                          borderRadius: '6px',
                          padding: '2px 8px',
                          boxSizing: 'border-box',
                        }}
                      >
                        Hippo Partner
                      </Box>
                    )}

                    <Box
                      sx={{
                        width: '96px',
                        height: '96px',
                        borderRadius: '18px',
                        backgroundColor: '#FFFFFF',
                        boxShadow:
                          '0px 1px 2px 0px #A2ACB133, 0px 12px 24px -4px #162F3B1F',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                      }}
                    >
                      {card.logo && (
                        <Image
                          width={96}
                          height={96}
                          src={card.logo}
                          alt={
                            card.imgAltTag ||
                            card.name ||
                            `Partner ${index + 1}`
                          }
                          title={card.imageAltTitle}
                          sx={{
                            maxWidth: '70%',
                            maxHeight: '70%',
                            objectFit: 'contain',
                          }}
                        />
                      )}
                    </Box>

                    <Typography
                      component="div"
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 700,
                        fontSize: '1.25rem',
                        lineHeight: '1.875rem',
                        letterSpacing: '0%',
                        color: '#162F3B',
                      }}
                    >
                      {card.name}
                    </Typography>

                    <Typography
                      component="div"
                      sx={{
                        ...richTextSx,
                        fontWeight: 400,
                        fontSize: '0.875rem',
                        lineHeight: '1.35rem',
                        letterSpacing: '-0.002em',
                        whiteSpace: 'pre-line',
                      }}
                      dangerouslySetInnerHTML={{ __html: card.body || '' }}
                    />

                    {card.redirect && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Typography
                          component="a"
                          href={card.redirect}
                          sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 600,
                            fontSize: '1rem',
                            lineHeight: '1.5rem',
                            letterSpacing: '-0.002em',
                            verticalAlign: 'middle',
                            color: '#162F3B',
                            textDecoration: 'none',
                            '&:hover': {
                              color: '#9EA900',
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          {card.ctaText || 'Read more'}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </ScrollFadeWrapper>
              </Collapse>
            );
          })}
        </Box>

        {data?.footer && (
          <Typography
            component="div"
            sx={{
              ...richTextSx,
              fontWeight: 400,
              fontSize: '1rem',
              lineHeight: '1.5rem',
              letterSpacing: '-0.002em',
              whiteSpace: 'pre-line',
              mt: '24px',
            }}
            dangerouslySetInnerHTML={{ __html: data.footer }}
          />
        )}
      </Box>
    </Box>
  );
};

export default LifeInsuranceCompaniesListMobileComponent;
